/**
 * All user-tunable state, plus its localStorage persistence.
 *
 * Kept deliberately flat and JSON-serialisable so it can be stored, diffed and unit tested
 * without any DOM involvement. Every consumer reads from a single `SettingsStore` instance
 * and subscribes for changes; nothing keeps its own private copy of a setting.
 */

import type { ControlPointMode } from '../tracking/types';
import { clamp } from '../tracking/types';

export type FacingMode = 'user' | 'environment';

/**
 * How the vibrato depth is decided.
 *
 *  - `off`     no vibrato at all: a pure, glassy tone.
 *  - `gentle`  a constant, subtle warmth.
 *  - `rich`    a constant, operatic wobble.
 *  - `hand`    the pitch hand shapes it: a fist is flat, an open hand sings.
 */
export type VibratoMode = 'off' | 'gentle' | 'rich' | 'hand';

export const VIBRATO_MODES: readonly VibratoMode[] = ['off', 'gentle', 'rich', 'hand'];

/** A calibrated near/far distance range in isotropic screen units. */
export interface ControlRange {
  near: number;
  far: number;
}

export interface PlacementSettings {
  /** Theremin centre in isotropic screen units (see videoCoordinates). */
  offsetX: number;
  offsetY: number;
  /** Uniform scale multiplier applied to the model. */
  scale: number;
  /** Yaw in radians (turn the instrument to face the player). */
  rotationY: number;
  /** Pitch in radians (tilt). */
  rotationX: number;
}

export interface Settings {
  version: number;

  // --- camera -------------------------------------------------------------
  facingMode: FacingMode;
  /** Mirror the preview. Defaults to true for the user-facing camera. */
  mirrorPreview: boolean;
  /** Requested capture height; lowered first when the device struggles. */
  captureHeight: 360 | 480 | 720;

  // --- control mapping ----------------------------------------------------
  /** Swap which hand drives pitch and which drives volume. */
  swapHands: boolean;
  invertPitch: boolean;
  invertVolume: boolean;
  controlPoint: ControlPointMode;
  /** 0.25 – 4. Compresses (>1) or expands (<1) the calibrated distance range. */
  sensitivity: number;
  /** 0 – 1. 0 = rawest/lowest latency, 1 = smoothest/laggiest. */
  smoothing: number;

  // --- audio --------------------------------------------------------------
  minFrequency: number;
  maxFrequency: number;
  /** Master output level, 0 – 1. */
  masterVolume: number;
  muted: boolean;
  vibratoMode: VibratoMode;
  /** Vibrato speed in Hz. */
  vibratoRate: number;
  /** Vibrato depth in cents at full amount; the mode scales this. */
  vibratoDepth: number;

  // --- calibration --------------------------------------------------------
  calibrated: boolean;
  pitchRange: ControlRange;
  volumeRange: ControlRange;

  // --- placement ----------------------------------------------------------
  placement: PlacementSettings;

  // --- diagnostics / a11y -------------------------------------------------
  showDiagnostics: boolean;
  /** Draw the tracked hand skeleton over the camera image. */
  showSkeleton: boolean;
  reducedMotion: boolean;
}

export const DEFAULT_PLACEMENT: PlacementSettings = {
  offsetX: 0,
  offsetY: -0.06,
  scale: 1,
  rotationY: 0,
  rotationX: 0,
};

/**
 * Defaults are chosen so the instrument is immediately playable without calibration:
 * the ranges below correspond to a hand roughly 0.1–0.55 screen-heights from an antenna,
 * which is comfortable for both a laptop webcam and a phone held at arm's length.
 */
export const DEFAULT_SETTINGS: Settings = {
  version: 4,

  facingMode: 'user',
  mirrorPreview: true,
  captureHeight: 480,

  swapHands: false,
  invertPitch: false,
  invertVolume: false,
  controlPoint: 'palm',
  sensitivity: 1,
  // Half-way up the slider: enough filtering that a still hand holds a still note, without
  // the lag being audible as a smear when the hand moves.
  smoothing: 0.5,

  minFrequency: 110,
  maxFrequency: 1760,
  masterVolume: 0.7,
  muted: false,
  vibratoMode: 'hand',
  vibratoRate: 5.4,
  vibratoDepth: 24,

  calibrated: false,
  pitchRange: { near: 0.1, far: 0.55 },
  volumeRange: { near: 0.08, far: 0.5 },

  placement: { ...DEFAULT_PLACEMENT },

  showDiagnostics: false,
  showSkeleton: true,
  reducedMotion: false,
};

export const SETTINGS_LIMITS = {
  frequency: { min: 40, max: 6000 },
  /** Keep at least this many octaves between min and max so the mapping stays usable. */
  minFrequencyRatio: 1.5,
  sensitivity: { min: 0.25, max: 4 },
  smoothing: { min: 0, max: 1 },
  masterVolume: { min: 0, max: 1 },
  /** Anything faster than ~9 Hz stops reading as vibrato and starts sounding like a fault. */
  vibratoRate: { min: 1, max: 9 },
  /** Cents. A semitone is 100, and a whole semitone of wobble is already theatrical. */
  vibratoDepth: { min: 0, max: 80 },
  scale: { min: 0.35, max: 3 },
  offset: { min: -1.6, max: 1.6 },
  rotation: { min: -Math.PI, max: Math.PI },
  /** Minimum usable span of a calibrated range, in screen units. */
  minRangeSpan: 0.05,
} as const;

const STORAGE_KEY = 'virtual-theremin.settings.v4';

/**
 * Older keys, newest first. A stored payload under one of these is read once and re-saved
 * under the current key, so upgrading the app never silently resets somebody's instrument.
 */
const LEGACY_STORAGE_KEYS: readonly string[] = ['virtual-theremin.settings.v3'];

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function pickNumber(value: unknown, fallback: number, min: number, max: number): number {
  return isFiniteNumber(value) ? clamp(value, min, max) : fallback;
}

function pickBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function pickEnum<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

function sanitizeRange(value: unknown, fallback: ControlRange): ControlRange {
  const raw = (value ?? {}) as Partial<ControlRange>;
  const near = pickNumber(raw.near, fallback.near, 0, 4);
  const far = pickNumber(raw.far, fallback.far, 0, 4);
  if (far - near < SETTINGS_LIMITS.minRangeSpan) {
    return { near, far: near + SETTINGS_LIMITS.minRangeSpan };
  }
  return { near, far };
}

function sanitizePlacement(value: unknown): PlacementSettings {
  const raw = (value ?? {}) as Partial<PlacementSettings>;
  const { offset, scale, rotation } = SETTINGS_LIMITS;
  return {
    offsetX: pickNumber(raw.offsetX, DEFAULT_PLACEMENT.offsetX, offset.min, offset.max),
    offsetY: pickNumber(raw.offsetY, DEFAULT_PLACEMENT.offsetY, offset.min, offset.max),
    scale: pickNumber(raw.scale, DEFAULT_PLACEMENT.scale, scale.min, scale.max),
    rotationY: pickNumber(raw.rotationY, DEFAULT_PLACEMENT.rotationY, rotation.min, rotation.max),
    rotationX: pickNumber(raw.rotationX, DEFAULT_PLACEMENT.rotationX, rotation.min, rotation.max),
  };
}

/** Fields that existed in an earlier version and are still honoured when reading. */
interface LegacySettings {
  /** v3 and earlier: a plain on/off vibrato switch. */
  vibrato?: unknown;
  /** v3 and earlier: the skeleton overlay was a debug-only "landmarks" toggle. */
  showLandmarks?: unknown;
}

/**
 * Coerce arbitrary (possibly corrupt or older-version) stored data into a valid `Settings`.
 * Never throws: unknown fields are dropped and out-of-range values are clamped.
 */
export function sanitizeSettings(input: unknown): Settings {
  const raw = (input ?? {}) as Partial<Settings> & LegacySettings;
  const limits = SETTINGS_LIMITS;

  // v3 stored vibrato as a boolean. `false` was a deliberate choice and is preserved; `true`
  // only meant "the old fixed depth", so those players are moved onto the new default.
  const legacyVibratoOff = raw.vibratoMode === undefined && raw.vibrato === false;

  let minFrequency = pickNumber(
    raw.minFrequency,
    DEFAULT_SETTINGS.minFrequency,
    limits.frequency.min,
    limits.frequency.max,
  );
  let maxFrequency = pickNumber(
    raw.maxFrequency,
    DEFAULT_SETTINGS.maxFrequency,
    limits.frequency.min,
    limits.frequency.max,
  );
  if (maxFrequency < minFrequency * limits.minFrequencyRatio) {
    maxFrequency = clamp(
      minFrequency * limits.minFrequencyRatio,
      limits.frequency.min,
      limits.frequency.max,
    );
    minFrequency = Math.min(minFrequency, maxFrequency / limits.minFrequencyRatio);
  }

  return {
    version: DEFAULT_SETTINGS.version,

    facingMode: pickEnum<FacingMode>(raw.facingMode, ['user', 'environment'], 'user'),
    mirrorPreview: pickBoolean(raw.mirrorPreview, DEFAULT_SETTINGS.mirrorPreview),
    captureHeight: ([360, 480, 720] as const).includes(raw.captureHeight as 360)
      ? (raw.captureHeight as 360 | 480 | 720)
      : DEFAULT_SETTINGS.captureHeight,

    swapHands: pickBoolean(raw.swapHands, DEFAULT_SETTINGS.swapHands),
    invertPitch: pickBoolean(raw.invertPitch, DEFAULT_SETTINGS.invertPitch),
    invertVolume: pickBoolean(raw.invertVolume, DEFAULT_SETTINGS.invertVolume),
    controlPoint: pickEnum<ControlPointMode>(raw.controlPoint, ['palm', 'indexTip'], 'palm'),
    sensitivity: pickNumber(
      raw.sensitivity,
      DEFAULT_SETTINGS.sensitivity,
      limits.sensitivity.min,
      limits.sensitivity.max,
    ),
    smoothing: pickNumber(
      raw.smoothing,
      DEFAULT_SETTINGS.smoothing,
      limits.smoothing.min,
      limits.smoothing.max,
    ),

    minFrequency,
    maxFrequency,
    masterVolume: pickNumber(
      raw.masterVolume,
      DEFAULT_SETTINGS.masterVolume,
      limits.masterVolume.min,
      limits.masterVolume.max,
    ),
    muted: pickBoolean(raw.muted, DEFAULT_SETTINGS.muted),
    vibratoMode: legacyVibratoOff
      ? 'off'
      : pickEnum<VibratoMode>(raw.vibratoMode, VIBRATO_MODES, DEFAULT_SETTINGS.vibratoMode),
    vibratoRate: pickNumber(
      raw.vibratoRate,
      DEFAULT_SETTINGS.vibratoRate,
      limits.vibratoRate.min,
      limits.vibratoRate.max,
    ),
    vibratoDepth: pickNumber(
      raw.vibratoDepth,
      DEFAULT_SETTINGS.vibratoDepth,
      limits.vibratoDepth.min,
      limits.vibratoDepth.max,
    ),

    calibrated: pickBoolean(raw.calibrated, DEFAULT_SETTINGS.calibrated),
    pitchRange: sanitizeRange(raw.pitchRange, DEFAULT_SETTINGS.pitchRange),
    volumeRange: sanitizeRange(raw.volumeRange, DEFAULT_SETTINGS.volumeRange),

    placement: sanitizePlacement(raw.placement),

    showDiagnostics: pickBoolean(raw.showDiagnostics, DEFAULT_SETTINGS.showDiagnostics),
    showSkeleton: pickBoolean(
      raw.showSkeleton,
      // v3 called it `showLandmarks` and defaulted it off; honour an explicit stored choice.
      pickBoolean(raw.showLandmarks, DEFAULT_SETTINGS.showSkeleton),
    ),
    reducedMotion: pickBoolean(raw.reducedMotion, DEFAULT_SETTINGS.reducedMotion),
  };
}

export type SettingsListener = (
  settings: Readonly<Settings>,
  changed: ReadonlySet<keyof Settings>,
) => void;

/** Minimal synchronous storage contract so tests can supply an in-memory implementation. */
export interface SettingsStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function safeLocalStorage(): SettingsStorage | null {
  try {
    const probe = '__theremin_probe__';
    globalThis.localStorage.setItem(probe, '1');
    globalThis.localStorage.removeItem(probe);
    return globalThis.localStorage;
  } catch {
    // Private mode, disabled storage, or a non-DOM environment. Settings become session-only.
    return null;
  }
}

/**
 * Observable settings container. Writes are debounced onto storage; reads are synchronous.
 */
export class SettingsStore {
  private state: Settings;

  private readonly listeners = new Set<SettingsListener>();

  private readonly storage: SettingsStorage | null;

  private writeHandle: ReturnType<typeof setTimeout> | null = null;

  private readonly changedKeys = new Set<keyof Settings>();

  constructor(
    storage: SettingsStorage | null = safeLocalStorage(),
    key: string = STORAGE_KEY,
    legacyKeys: readonly string[] = LEGACY_STORAGE_KEYS,
  ) {
    this.storage = storage;
    this.storageKey = key;
    this.legacyKeys = legacyKeys;
    this.state = this.load();
  }

  private readonly storageKey: string;

  private readonly legacyKeys: readonly string[];

  private load(): Settings {
    const storage = this.storage;
    if (!storage) return sanitizeSettings(undefined);
    for (const key of [this.storageKey, ...this.legacyKeys]) {
      try {
        const raw = storage.getItem(key);
        if (!raw) continue;
        // The sanitizer migrates renamed fields, so an older payload is simply read and then
        // written back under the current key by the next update.
        return sanitizeSettings(JSON.parse(raw));
      } catch {
        // Corrupt entry: fall through to the next candidate, then to defaults.
      }
    }
    return sanitizeSettings(undefined);
  }

  get current(): Readonly<Settings> {
    return this.state;
  }

  subscribe(listener: SettingsListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Apply a partial update. Values are sanitized; no-op updates do not notify. */
  update(patch: Partial<Settings>): void {
    const next = sanitizeSettings({ ...this.state, ...patch });
    const changed = new Set<keyof Settings>();
    for (const key of Object.keys(next) as (keyof Settings)[]) {
      const before = this.state[key];
      const after = next[key];
      const equal =
        typeof before === 'object' && before !== null
          ? JSON.stringify(before) === JSON.stringify(after)
          : before === after;
      if (!equal) changed.add(key);
    }
    if (changed.size === 0) return;

    this.state = next;
    for (const key of changed) this.changedKeys.add(key);
    this.emit(changed);
    this.scheduleWrite();
  }

  /** Restore factory defaults, including placement and calibration. */
  reset(): void {
    this.state = sanitizeSettings(undefined);
    const all = new Set(Object.keys(this.state) as (keyof Settings)[]);
    this.emit(all);
    this.scheduleWrite();
  }

  private emit(changed: ReadonlySet<keyof Settings>): void {
    for (const listener of [...this.listeners]) {
      try {
        listener(this.state, changed);
      } catch (error) {
        console.error('[settings] listener failed', error);
      }
    }
  }

  private scheduleWrite(): void {
    if (!this.storage) return;
    if (this.writeHandle !== null) clearTimeout(this.writeHandle);
    this.writeHandle = setTimeout(() => {
      this.writeHandle = null;
      this.flush();
    }, 250);
  }

  /** Persist immediately (used on pagehide so nothing is lost when the tab is discarded). */
  flush(): void {
    if (!this.storage) return;
    try {
      this.storage.setItem(this.storageKey, JSON.stringify(this.state));
    } catch {
      // Quota or privacy restrictions — the app keeps working with in-memory settings.
    }
  }

  dispose(): void {
    if (this.writeHandle !== null) {
      clearTimeout(this.writeHandle);
      this.writeHandle = null;
      this.flush();
    }
    this.listeners.clear();
  }
}
